import * as React from 'react';
import { observer } from 'mobx-react';
import { ClientItem, ClientMessage } from 'owa-mail-store';
import { isMeetingRequest, isMeetingResponse } from 'owa-meeting-message';
import MeetingRequestMessageType from 'owa-service/lib/contract/MeetingRequestMessageType';
import MeetingResponseMessageType from 'owa-service/lib/contract/MeetingResponseMessageType';
import formatMeetingTimeSpan from 'owa-timeformat/lib/utils/formatMeetingTimeSpan';
import {
    newMeetingTimeLabel,
    newMeetingLocationLabel,
    proposedNewTimeLabel,
    meetingSummaryPart,
} from './PreviewSummary.locstring.json';
import loc, { formatToArray } from 'owa-localize';

const styles = require('./PreviewSummary.scss');

export interface PreviewSummaryProps {
    item: ClientItem;
    isNodePending: boolean;
    printAllowed: boolean;
    renderPreview: (
        item: ClientMessage,
        isNodePending: boolean,
        defaultIfEmpty: boolean,
        printAllowed: boolean
    ) => JSX.Element;
}

@observer
export default class PreviewSummary extends React.Component<PreviewSummaryProps, any> {
    render() {
        const { item, isNodePending, printAllowed, renderPreview } = this.props;

        let useDefaultPreviewText = false;

        if (isMeetingRequest(item.ItemClass)) {
            const message: MeetingRequestMessageType = this.props.item as MeetingRequestMessageType;
            if (
                message.ChangeHighlights &&
                (message.ChangeHighlights.HasEndTimeChanged ||
                    message.ChangeHighlights.HasStartTimeChanged ||
                    message.ChangeHighlights.HasLocationChanged)
            ) {
                const timePart = this.renderMeetingTimeSummary(message);
                const locationPart = this.renderMeetingLocationSummary(message);
                return (
                    <div className={styles.previewBody}>
                        {timePart}
                        {locationPart}
                    </div>
                );
            }
            useDefaultPreviewText = true;
        } else if (isMeetingResponse(item.ItemClass)) {
            const message: MeetingResponseMessageType = this.props
                .item as MeetingResponseMessageType;
            if (message.ProposedStart && message.ProposedEnd) {
                return (
                    <div className={styles.previewBody}>
                        {this.renderTimeProposalSummary(message)}
                    </div>
                );
            }
        }
        return renderPreview(
            item,
            isNodePending,
            useDefaultPreviewText /* defaultIfEmpty */,
            printAllowed
        );
    }

    private renderMeetingTimeSummary(message: MeetingRequestMessageType): JSX.Element {
        if (
            !(
                message.ChangeHighlights.HasEndTimeChanged ||
                message.ChangeHighlights.HasStartTimeChanged
            )
        ) {
            return null;
        }

        return this.renderSummaryPart(
            loc(newMeetingTimeLabel),
            formatMeetingTimeSpan(message.Start, message.End, message.Recurrence)
        );
    }

    private renderMeetingLocationSummary(message: MeetingRequestMessageType): JSX.Element {
        if (!message.ChangeHighlights.HasLocationChanged) {
            return null;
        }

        return this.renderSummaryPart(loc(newMeetingLocationLabel), message.Location.DisplayName);
    }

    private renderTimeProposalSummary(message: MeetingResponseMessageType): JSX.Element {
        return this.renderSummaryPart(
            loc(proposedNewTimeLabel),
            formatMeetingTimeSpan(message.ProposedStart, message.ProposedEnd)
        );
    }

    private renderSummaryPart(label: string, content: string): JSX.Element {
        return (
            <span className={styles.previewSummaryPart}>
                {formatToArray(loc(meetingSummaryPart), label, content)}
            </span>
        );
    }
}
